package com.essntl.features.currency.ui.allowedcurrencies

import androidx.compose.foundation.border
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import com.essntl.core.settings.ui.AllowedCurrenciesEvent
import com.essntl.core.settings.ui.AllowedCurrenciesNavigation
import com.essntl.core.settings.ui.AllowedCurrenciesState
import com.essntl.uikit.components.button.secondary.EsLargeSecondaryButton
import com.essntl.uikit.components.iconbutton.outline.EsSmallOutlineIconButton
import com.essntl.uikit.components.icons.EsIcons
import com.essntl.uikit.components.radiobutton.EsPrimaryRadioButton
import com.essntl.uikit.components.spinner.EsSpinner
import com.essntl.uikit.components.utils.EsConstants
import com.essntl.uikit.components.utils.spacing
import com.essntl.uikit.theme.esColorScheme
import com.essntl.uikit.theme.esTypography

@Composable
fun AllowedCurrenciesScreen(
    state: AllowedCurrenciesState,
    onEvent: (AllowedCurrenciesEvent) -> Unit,
    onNavigation: (AllowedCurrenciesNavigation) -> Unit,
) {
    Column(
        verticalArrangement = Arrangement.Top,
        horizontalAlignment = Alignment.Start,
        modifier = Modifier
            .fillMaxSize()
            .padding(2.5f.spacing),
    ) {
        Text(
            text = "Currency",
            color = MaterialTheme.esColorScheme.textPrimary,
            style = MaterialTheme.esTypography.h4SemiBold20,
        )

        Spacer(modifier = Modifier.height(5.spacing))

        Column(
            modifier = Modifier.fillMaxWidth(),
        ) {
            Row(
                modifier = Modifier.fillMaxWidth(),
            ) {
                Text(
                    text = "Your supported currencies",
                    color = MaterialTheme.esColorScheme.textPrimary,
                    style = MaterialTheme.esTypography.p1Medium16,
                    modifier = Modifier.weight(6f),
                )

                Text(
                    text = "Default currency",
                    color = MaterialTheme.esColorScheme.textPrimary,
                    style = MaterialTheme.esTypography.p1Medium16,
                    modifier = Modifier.weight(2f),
                )

                Text(
                    text = "Delete",
                    color = MaterialTheme.esColorScheme.textPrimary,
                    style = MaterialTheme.esTypography.p1Medium16,
                    modifier = Modifier.weight(2f),
                )
            }

            Spacer(modifier = Modifier.height(3.spacing))

            if (state.getAllowedCurrencies.isLoading) {
                Box(
                    contentAlignment = Alignment.Center,
                    modifier = Modifier
                        .fillMaxSize(),
                ) {
                    EsSpinner()
                }
            } else if (state.getAllowedCurrencies.isFailure) {
                Box(
                    contentAlignment = Alignment.Center,
                    modifier = Modifier
                        .fillMaxSize(),
                ) {
                    Text(
                        text = "Something goes wrong!",
                    )
                }
            } else
                LazyColumn(
                    verticalArrangement = Arrangement.spacedBy(EsConstants.ListColumnHeaderHeight),
                    contentPadding = PaddingValues(vertical = EsConstants.SectionVerticalPadding),
                    modifier = Modifier.fillMaxWidth(),
                ) {
                    items(state.getAllowedCurrencies.data?.allowed.orEmpty()) { allowed ->
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                        ) {
                            Box(modifier = Modifier.weight(6f)) {
                                Box(
                                    modifier = Modifier
                                        .fillMaxWidth(0.8f)
                                        .border(
                                            width = 1.dp,
                                            color = MaterialTheme.esColorScheme.borderPrimary,
                                            shape = RoundedCornerShape(10.dp),
                                        ).padding(1.5.spacing),
                                ) {
                                    Text(
                                        text = allowed,
                                        color = MaterialTheme.esColorScheme.textTertiary,
                                        style = MaterialTheme.esTypography.p1Medium16,
                                        modifier = Modifier.align(Alignment.CenterStart),
                                    )
                                }
                            }

                            Box(
                                modifier = Modifier
                                    .padding(1.5.spacing)
                                    .weight(2f),
                            ) {
                                EsPrimaryRadioButton(
                                    selected = allowed == state.getAllowedCurrencies.data?.default,
                                    loading = state.updateLoading.contains(allowed),
                                    onSelectedChange = {
                                        if (allowed != state.getAllowedCurrencies.data?.default)
                                            onEvent(AllowedCurrenciesEvent.DefaultCurrency(allowed))
                                    },
                                    modifier = Modifier.align(Alignment.CenterStart),
                                )
                            }

                            Box(
                                modifier = Modifier
                                    .padding(1.5.spacing)
                                    .weight(2f),
                            ) {
                                EsSmallOutlineIconButton(
                                    iconRes = EsIcons.Trash,
                                    enabled = allowed != state.getAllowedCurrencies.data?.default,
                                    loading = state.deleteLoading.contains(allowed),
                                    onClick = {
                                        onEvent(AllowedCurrenciesEvent.DeleteCurrency(allowed))
                                    },
                                    modifier = Modifier.align(Alignment.CenterStart),
                                )
                            }
                        }
                    }
                }
        }

        Spacer(modifier = Modifier.height(5.spacing))

        EsLargeSecondaryButton(
            text = "Add a currency",
            prefixIconRes = EsIcons.Add,
            onClick = {
                onNavigation(AllowedCurrenciesNavigation.AddCurrency)
            },
            modifier = Modifier.padding(top = 1.spacing),
        )
    }
}